// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantService } from '../../services/tenant.service';
import { ConfigurationService } from '../../services/configuration.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '../../services/toast.service';
import { VerifyPinRequest } from '../../models/auth.models';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  authType: string = 'public'; // Default auth type
  showPinField: boolean = false; // Show PIN field only if authType is 'JwtAndPin'
  pinForm!: FormGroup;
  isSubmitting: boolean = false;
  appConfigurationId: string | null = null; // Will store the AppConfigurationId from config
  showPin: boolean = false; // Toggle PIN visibility

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private tenantService: TenantService,
    private configurationService: ConfigurationService,
    private formBuilder: FormBuilder,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    // Initialize PIN form
    this.pinForm = this.formBuilder.group({
      pin: ['', [Validators.required, Validators.minLength(4), Validators.pattern(/^\d+$/)]]
    });

    // Load menu configuration to get authType for the current menu/page
    this.loadAuthTypeConfiguration();
  }

  /**
   * Load the menu configuration to determine the authentication type
   * This helps determine if PIN is required
   */
  private loadAuthTypeConfiguration(): void {
    // Get the return URL from query params to determine which menu is being accessed
    const redirectTo = this.route.snapshot.queryParams['redirectTo'] || '';

    // If we can't extract from URL, you might want to pass it via another route param
    const menuKey = this.route.snapshot.queryParams['menuKey'] || '';

    if (menuKey) {
      this.configurationService.getMenuByKey(menuKey).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: (menuConfig) => {
          if (menuConfig && menuConfig.authType) {
            this.authType = menuConfig.authType;
            const authTypeStr = String(this.authType).trim().toLowerCase();
            this.showPinField = authTypeStr === 'jwtandpin';
          }
        },
        error: (error) => {
          console.error('Error loading menu configuration:', error);
          // Default behavior if config fails to load
          this.showPinField = false;
        }
      });
    }

    // Also load the app configuration ID from the full configuration
    this.configurationService.getConfiguration().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (config) => {
        // Extract AppConfigurationId from configuration
        // This will be used for PIN verification requests
        if (config && config['id']) {
          this.appConfigurationId = config['id'];
        }
      },  
      error: (error) => {
        console.error('Error loading app configuration:', error);
      }
    });
  }

  /**
   * Extract menu key from URL path
   * E.g., "/gallery" -> "gallery", "/gallery/category/id" -> "gallery"
   */
  private extractMenuKeyFromUrl(url: string): string {
    if (!url) return '';
    const segments = url.split('/').filter(s => s);
    return segments[0] || '';
  }

  loginWithGoogle() {
    const redirectTo = this.route.snapshot.queryParams['redirectTo'] || `/${this.tenantService.getTenantKey()}`;
    const clientId = this.tenantService.getTenantId() ?? this.extractMenuKeyFromUrl(redirectTo);
    
    this.authService.googleLogin(clientId, redirectTo);
  }

  /**
   * Toggle PIN visibility
   */
  togglePinVisibility(): void {
    this.showPin = !this.showPin;
  }

  /**
   * Submit PIN for verification
   * Implements the PIN verification flow with HttpOnly cookies
   */
  submitPin(): void {
    if (!this.pinForm.valid) {
      this.toastService.error('Please enter a valid PIN (4-8 digits)');
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    if (!this.appConfigurationId) {
      this.toastService.error('Application configuration not loaded. Please refresh and try again.');
      return;
    }

    this.isSubmitting = true;
    const pin = this.pinForm.get('pin')?.value as string;

    // Get the menuKey from query params
    const menuKey = this.route.snapshot.queryParams['menuKey'] || this.extractMenuKeyFromUrl(
      this.route.snapshot.queryParams['redirectTo'] || ''
    );

    // Build the PIN verification request according to backend specification
    const verifyPinRequest: VerifyPinRequest = {
      appConfigurationId: this.appConfigurationId,
      menuKey: menuKey || '',
      groupKey: undefined,
      pin: pin
    };

    // Call the PIN verification endpoint
    // POST /api/pin/verify
    // Backend will set HttpOnly cookie automatically
    this.authService.verifyPinAndWaitForAuth(verifyPinRequest).then((success) => {
      if (!success) {
        this.toastService.error('Invalid PIN. Please try again.');
        this.isSubmitting = false;
        this.pinForm.get('pin')?.reset();
        return;
      }

      // Reset submit state before navigation
      this.isSubmitting = false;
      
      // Show success message
      this.toastService.success('PIN verified successfully!');
      
      // Navigate to returnUrl from query params (PIN flow)
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || `/${this.tenantService.getTenantKey()}`;
      this.router.navigateByUrl(returnUrl).catch((err) => {
        console.error('Navigation to returnUrl failed:', err);
        // Fallback to home page
        this.router.navigate([`/${this.tenantService.getTenantKey()}`]);
      });
    }).catch((error) => {
      console.error('PIN verification failed:', error);
      this.isSubmitting = false;

      // Handle different error scenarios
      let errorMessage = 'PIN verification failed';
      
      if (error.status === 400) {
        errorMessage = 'Invalid PIN format';
      } else if (error.status === 401) {
        errorMessage = 'Invalid PIN or access denied';
      } else if (error.status === 429) {
        // Rate limiting - too many attempts
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error?.error?.errors?.length > 0) {
        errorMessage = error.error.errors[0];
      }

      this.toastService.error(errorMessage);
      
      // Clear the PIN field for security
      this.pinForm.get('pin')?.reset();
    });
  }
}
